import { useReducer, useEffect } from 'react';
import { getFirestore } from 'firebase/firestore';
import { firebase } from '../../../../services/firebase';
import { doc, updateDoc, setDoc, collection, getDoc } from 'firebase/firestore';
import { logEvent, logError } from '../../../../utils/analytics';
import { v4 as uuidv4 } from "uuid";

const initialState = {
    messages: [],
    newMessage: '',
    isClosed: false,
    selectedTag: null,
    editingMessage: null,
    hasSentMessage: false,
    consecutiveBuzzCount: 0,
    isSending: false,
    lastBuzzReset: null,
    showChatJourney: false,
    chatJourney: null,
    currentJourneyNode: null,
    parentJourneyNode: null,
    showSpamDialog: false,
    showSpamWarningDialog: false,
    showBlockedDialog: false,
    showCloseChatDialog: false,
    showBlockUserDialog: false,
    isBionicReading: false,
    showNameChangeDialog: false
};

// Function to initialize chat state based on shop settings
const initializeChatState = async (shopId) => {
    try {
        const db = getFirestore();
        const shopDoc = await getDoc(doc(db, 'users', shopId));
        if (!shopDoc.exists()) {
            const { logError } = await import('../../../../utils/analytics');
            logError('CHAT_SHOP_NOT_FOUND', new Error('Shop document not found'), { shopId });
            return initialState;
        }
        const shopData = shopDoc.data();

        // Check if chat journey is disabled in shop settings
        const chatJourneyDisabled = shopData?.chatSettings?.disableChatJourney || false;

        // Get chat journey data from the correct path
        const chatJourneysRef = doc(db, 'users', shopId, 'chatJourneys', 'default');
        const chatJourneyDoc = await getDoc(chatJourneysRef);
        const chatJourneyData = chatJourneyDoc.data();

        // Check if chat journey is enabled
        const isChatJourneyEnabled = chatJourneyData?.isEnabled || false;

        // Get the latest version if available
        const latestVersion = chatJourneyData?.latestVersion;
        let chatJourney = null;

        if (latestVersion) {
            const versionRef = doc(db, 'users', shopId, 'chatJourneys', latestVersion);
            const versionDoc = await getDoc(versionRef);
            chatJourney = versionDoc.data();
        }

        const shouldShowChatJourney = !chatJourneyDisabled && isChatJourneyEnabled && !!chatJourney;
        return {
            ...initialState,
            showChatJourney: shouldShowChatJourney,
            chatJourney: shouldShowChatJourney ? chatJourney : null,
        };
    } catch (error) {
        logError('useChatState: initialize chat state', error);
        return initialState;
    }
};

function chatReducer(state, action) {
    switch (action.type) {
        case 'SET_JOURNEY_MESSAGE':
            return {
                ...state,
                messages: [
                    ...state.messages,
                    {
                        type: 'user',
                        text: action.payload.response,
                        sender: action.payload.sender,
                        timestamp: new Date()
                    }
                ]
            };
        case 'SET_MESSAGES':
            return {
                ...state,
                messages: action.payload.map(message => ({
                    ...message,
                    sender: message.sender || 'shop',
                    timestamp: message?.timestamp?.seconds ? new Date(message.timestamp.seconds * 1000) : message.timestamp
                }))
            };
        case 'SET_NEW_MESSAGE':
            return { ...state, newMessage: action.payload };
        case 'SET_CLOSED':
            return { ...state, isClosed: action.payload };
        case 'SET_SELECTED_TAG':
            return { ...state, selectedTag: action.payload };
        case 'SET_EDITING_MESSAGE':
            return { ...state, editingMessage: action.payload };
        case 'SET_HAS_SENT_MESSAGE':
            return { ...state, hasSentMessage: action.payload };
        case 'SET_CONSECUTIVE_BUZZ_COUNT':
            return { ...state, consecutiveBuzzCount: action.payload };
        case 'SET_SENDING':
            return { ...state, isSending: action.payload };
        case 'SET_LAST_BUZZ_RESET':
            return { ...state, lastBuzzReset: action.payload };
        case 'SET_SHOW_CHAT_JOURNEY':
            return { ...state, showChatJourney: action.payload };
        case 'SET_CHAT_JOURNEY':
            return { ...state, chatJourney: action.payload };
        case 'SET_CURRENT_JOURNEY_NODE':
            return { ...state, currentJourneyNode: action.payload };
        case 'SET_PARENT_JOURNEY_NODE':
            return { ...state, parentJourneyNode: action.payload };
        case 'SET_SHOW_SPAM_DIALOG':
            return { ...state, showSpamDialog: action.payload };
        case 'SET_SHOW_SPAM_WARNING_DIALOG':
            return { ...state, showSpamWarningDialog: action.payload };
        case 'SET_SHOW_BLOCKED_DIALOG':
            return { ...state, showBlockedDialog: action.payload };
        case 'SET_SHOW_CLOSE_CHAT_DIALOG':
            return { ...state, showCloseChatDialog: action.payload };
        case 'SET_SHOW_BLOCK_USER_DIALOG':
            return { ...state, showBlockUserDialog: action.payload };
        case 'SET_BIONIC_READING':
            return { ...state, isBionicReading: action.payload };
        case 'SET_SHOW_NAME_CHANGE_DIALOG':
            return { ...state, showNameChangeDialog: action.payload };
        case 'INITIALIZE_STATE':
            return action.payload;
        default:
            return state;
    }
}

export const useChatState = (shopId, guestId) => {
    const [state, dispatch] = useReducer(chatReducer, initialState);


    const sendMessage = async (userId, guestId, senderName, uniqueId, isOwner, shopperName, message) => {
        // message can be either string (legacy) or {text, images}
        let text = '', images = [];
        if (typeof message === 'string') {
            text = message;
        } else if (typeof message === 'object' && message !== null) {
            text = message.text || '';
            images = Array.isArray(message.images) ? message.images : [];
        }
        const messageStr = String(message || '');
        if (!messageStr.trim() || !userId || !guestId || state.isClosed || state.isSending) return;

        try {
            dispatch({ type: 'SET_SENDING', payload: true });
            dispatch({ type: 'SET_CONSECUTIVE_BUZZ_COUNT', payload: 0 });

            const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);
            const chatData = {
                guestId,
                uniqueId,
                lastMessageTimestamp: new Date(),
                ...((!isOwner && shopperName) ? { shopperName } : {})
            };

            await setDoc(chatRef, chatData, { merge: true });

            const messagesCollection = collection(firebase, `users/${userId}/chats/${guestId}/messages`);
            const messageId = uuidv4();
            const messageRef = doc(messagesCollection, messageId);
            const messageData = {
                id: messageId,
                text,
                images, // Save images array for image messages
                sender: isOwner ? senderName : shopperName,
                timestamp: new Date(),
                status: isOwner ? 'read' : 'unread',
                buzzed: false,
                uniqueId,
                type: isOwner ? 'user' : 'guest',
                ...((!isOwner && shopperName) ? { shopperName } : {})
            };

            await setDoc(messageRef, messageData);

            dispatch({ type: 'SET_NEW_MESSAGE', payload: '' });
            if (!isOwner && !state.hasSentMessage) {
                dispatch({ type: 'SET_HAS_SENT_MESSAGE', payload: true });
            }
            logEvent('sendMessage', { isOwner, messageId });
        } catch (error) {
            logError('sendMessage', error);
        } finally {
            dispatch({ type: 'SET_SENDING', payload: false });
        }
    };

    const buzzMessage = async (userId, guestId, senderName, uniqueId, isOwner, shopperName) => {
        if (!userId || !guestId || state.isClosed || state.isSending) return;

        try {
            dispatch({ type: 'SET_SENDING', payload: true });
            const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);
            const chatDoc = await getDoc(chatRef);
            const lastBuzzReset = chatDoc.data()?.lastBuzzReset || null;

            const now = new Date();
            const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

            if (!lastBuzzReset || new Date(lastBuzzReset) < fiveMinutesAgo) {
                dispatch({ type: 'SET_CONSECUTIVE_BUZZ_COUNT', payload: 1 });
                await updateDoc(chatRef, { lastBuzzReset: now });
                await createBuzzMessage(userId, guestId, senderName, uniqueId, isOwner, shopperName);
                logEvent('buzzMessage', { buzzCount: 1 });
                dispatch({ type: 'SET_SENDING', payload: false });
                return;
            }

            const newBuzzCount = state.consecutiveBuzzCount + 1;
            dispatch({ type: 'SET_CONSECUTIVE_BUZZ_COUNT', payload: newBuzzCount });
            await updateDoc(chatRef, { lastBuzzReset: now });

            if (newBuzzCount >= 3) {
                await updateDoc(chatRef, { tag: 'Spam', uniqueId });
                dispatch({ type: 'SET_SHOW_SPAM_DIALOG', payload: true });
                dispatch({ type: 'SET_SENDING', payload: false });
                return;
            }

            if (newBuzzCount >= 2) {
                dispatch({ type: 'SET_SHOW_SPAM_WARNING_DIALOG', payload: true });
                dispatch({ type: 'SET_SENDING', payload: false });
                return;
            }

            await createBuzzMessage(userId, guestId, senderName, uniqueId, isOwner, shopperName);
            logEvent('buzzMessage', { buzzCount: newBuzzCount });
        } catch (error) {
            logError('buzzMessage', error);
        } finally {
            dispatch({ type: 'SET_SENDING', payload: false });
        }
    };

    const createBuzzMessage = async (userId, guestId, senderName, uniqueId, isOwner, shopperName) => {
        const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);

        // Ensure we have valid names
        const finalShopperName = !isOwner ? (senderName || `Guest-${guestId}`) : null;

        await setDoc(chatRef, {
            guestId,
            uniqueId,
            lastMessageTimestamp: new Date(),
            ...(finalShopperName ? { shopperName: finalShopperName } : {})
        }, { merge: true });

        const messagesCollection = collection(firebase, `users/${userId}/chats/${guestId}/messages`);
        const messageId = uuidv4();
        const messageRef = doc(messagesCollection, messageId);
        const messageData = {
            id: messageId,
            text: "🔔 Buzzed the shop!",
            sender: senderName,
            timestamp: new Date(),
            status: isOwner ? 'read' : 'unread',
            buzzed: true,
            buzzTimestamp: new Date(),
            uniqueId,
            type: 'guest',
            ...(finalShopperName ? { shopperName: finalShopperName } : {})
        };

        await setDoc(messageRef, messageData);

        if (!isOwner && !state.hasSentMessage) {
            dispatch({ type: 'SET_HAS_SENT_MESSAGE', payload: true });
        }
    };

    const handleNameChange = async (userId, guestId, uniqueId, newName, navigate) => {
        try {
            const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);
            const chatDoc = await getDoc(chatRef);

            if (chatDoc.exists()) {
                // Update the current chat with the name change tag
                await updateDoc(chatRef, {
                    tag: `Changed nickname to ${newName}`,
                    closed: true,
                    uniqueId
                });

                // Create a new chat with the new name
                const newGuestId = `${Date.now()}-${uuidv4()}`;
                const newChatRef = doc(firebase, `users/${userId}/chats/${newGuestId}`);

                await setDoc(newChatRef, {
                    guestId: newGuestId,
                    lastMessageTimestamp: new Date(),
                    shopperName: newName,
                    uniqueId,
                    closed: false
                });

                // Update local storage and state
                localStorage.setItem("guestId", newGuestId);
                localStorage.setItem("shopperName", newName);

                // Clear the state
                dispatch({ type: 'SET_MESSAGES', payload: [] });
                dispatch({ type: 'SET_NEW_MESSAGE', payload: '' });
                dispatch({ type: 'SET_CLOSED', payload: false });
                dispatch({ type: 'SET_SELECTED_TAG', payload: null });

                // Navigate to the new chat
                navigate(`/chat?user=${userId}&guestId=${newGuestId}`);
                logEvent('nameChange', { newName });
                return true;
            }
        } catch (error) {
            logError('handleNameChange', error);
            return false;
        }
    };

    // Add a new function to process messages with correct sender names
    const processMessages = (messages, shopName, shopperName) => {
        return messages.map(message => ({
            ...message,
            sender: message.type === 'user' ? shopName : (message.type === 'user' ? shopperName : message.sender)
        }));
    };


    return {
        state,
        dispatch,
        sendMessage,
        buzzMessage,
        handleNameChange,
        processMessages
    };
}; 